import { Fragment, useState } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../portal/context/auth-context';
import fullLogo from '../../assets/images/simpleLogo.jpg';
import { Link, useNavigate } from 'react-router-dom';
import SearchBar from './global-search/search-bar/SearchBar';
import AccountChooserModal from './AccountChooserModal';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function NavBar() {
    const { logout, login, currentUser, switchAccount, dashboardPartsSearch, setDashboardPartsSearch } = useAuth();
    const navigate = useNavigate();
    
    const [switchOpen, setSwitchOpen] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const allByEmail = Array.isArray(currentUser?.userInfoAllEmail)
        ? currentUser.userInfoAllEmail
        : Array.isArray(currentUser?.userInfoAll)
        ? currentUser.userInfoAll
        : [];

    const accountsForChooser = allByEmail.filter((a) => a?.Id !== currentUser?.Id);

    const handleConfirmAccount = (contact) => {
        setErrorMsg('');
        if (!contact?.Id) return;

        setSwitchOpen(false);

        if (contact?.CP_Allow_Portal_Access__c) {
            const userForLogin = {
                ...contact,
                Owner: currentUser?.Owner,
                userInfoAllEmail: currentUser?.userInfoAllEmail ?? [],
                userInfoAll: currentUser?.userInfoAll ?? [],
            };

            setTimeout(() => {
                const suppress = !contact?.CP_Last_Sign_In__c;
                login(userForLogin, true, false, { suppressFirstLogin: suppress });
                navigate('/');
            }, 250);
            return;
        }

        switchAccount(contact);
        navigate('/');
    };

    const locationReload = (targetPath = '/') => {
        if (dashboardPartsSearch) {
            if (window.location.pathname === '/' && targetPath === '/') {
                setDashboardPartsSearch('exiting');
                return;
            }
            setDashboardPartsSearch(false);
        }

        navigate(targetPath);
    };

    if (currentUser?.firstLogin === true) {
        return null;
    }

    return (
        <Disclosure as='nav' className='bg-white shadow'>
            {({ open }) => (
                <>
                    <div className='px-4 mx-auto sm:px-6 lg:px-8'>
                        <div className='flex justify-between h-16'>
                            <div className='flex'>
                                <div className='flex flex-shrink-0 items-center'>
                                    <div onClick={() => locationReload('/')} className='cursor-pointer'>
                                        <img className='block w-auto h-8 lg:hidden' src={fullLogo} alt='Your Company' />
                                        <img className='hidden w-auto h-12 lg:block' src={fullLogo} alt='Your Company' />
                                    </div>
                                </div>
                                <div className='hidden sm:ml-6 sm:flex sm:space-x-8'>
                                    <div
                                        onClick={() => locationReload('/')}
                                        className={classNames(
                                            window.location.pathname === '/' ? 'border-blue-primary' : 'border-transparent',
                                            'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium text-gray-900 cursor-pointer'
                                        )}
                                    >
                                        Parts Search
                                    </div>
                                    <div
                                        onClick={() => locationReload('/quotes')}
                                        className={classNames(
                                            window.location.pathname === '/quotes'
                                                ? 'border-blue-primary'
                                                : 'border-transparent',
                                            'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium text-gray-900 cursor-pointer'
                                        )}
                                    >
                                        Parts Search History
                                    </div>
                                    <div
                                        onClick={() => locationReload('/orders')}
                                        className={classNames(
                                            window.location.pathname === '/orders'
                                                ? 'border-blue-primary'
                                                : 'border-transparent',
                                            'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium text-gray-900 cursor-pointer'
                                        )}
                                    >
                                        My Orders
                                    </div>
                                </div>
                            </div>
                            <SearchBar />
                            <div className='hidden sm:ml-6 sm:flex sm:items-center'>
                                <div className='pl-6'>
                                    <p className='text-sm font-medium text-gray-700'>
                                        Welcome, {currentUser?.Name || 'User'}
                                    </p>
                                    <p
                                        className='text-xs text-gray-500 -mt-0.5 truncate max-w-[220px]'
                                        title={currentUser?.Account?.Name || ''}
                                    >
                                        {currentUser?.Account?.Name || '—'}
                                    </p>
                                </div>

                                <Menu as='div' className='relative ml-3'>
                                    <div>
                                        <Menu.Button className='flex text-sm bg-white rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2 pointer'>
                                            <span className='sr-only'>Open user menu</span>
                                            <div
                                                type='button'
                                                className='p-1 text-gray-400 bg-white rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2'
                                            >
                                                <span className='sr-only'>View notifications</span>
                                                <Cog8ToothIcon className='w-6 h-6' aria-hidden='true' />
                                            </div>
                                        </Menu.Button>
                                    </div>
                                    <Transition
                                        as={Fragment}
                                        enter='transition ease-out duration-200'
                                        enterFrom='transform opacity-0 scale-95'
                                        enterTo='transform opacity-100 scale-100'
                                        leave='transition ease-in duration-75'
                                        leaveFrom='transform opacity-100 scale-100'
                                        leaveTo='transform opacity-0 scale-95'
                                    >
                                        <Menu.Items className='absolute right-0 z-20 py-1 mt-2 w-48 bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg origin-top-right focus:outline-none'>
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <Link
                                                        to='/settings?tab=Account'
                                                        className={classNames(
                                                            active ? 'bg-gray-100' : '',
                                                            'block px-4 py-2 text-sm text-gray-700'
                                                        )}
                                                    >
                                                        My Account
                                                    </Link>
                                                )}
                                            </Menu.Item>
                                            {accountsForChooser.length > 0 && (
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <a
                                                            onClick={() => {
                                                                setSwitchOpen(true);
                                                                setErrorMsg('');
                                                            }}
                                                            className={classNames(
                                                                active ? 'bg-gray-100' : '',
                                                                'block px-4 py-2 text-sm text-gray-700 cursor-pointer'
                                                            )}
                                                        >
                                                            Switch account
                                                        </a>
                                                    )}
                                                </Menu.Item>
                                            )}
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <a
                                                        onClick={logout}
                                                        className={classNames(
                                                            active ? 'bg-gray-100' : '',
                                                            'block px-4 py-2 text-sm text-gray-700 cursor-pointer'
                                                        )}
                                                    >
                                                        Sign out
                                                    </a>
                                                )}
                                            </Menu.Item>
                                        </Menu.Items>
                                    </Transition>
                                </Menu>
                                <div className='px-3 text-sm text-center'>
                                    <p>Contact Us: {currentUser?.Owner?.Name || 'Sales Team'}</p>
                                    <a
                                        className='underline text-blue-secondary'
                                        href={`mailto:${currentUser?.Owner?.Email || 'sales@pac-air.com'}`}
                                    >
                                        {currentUser?.Owner?.Email || 'sales@pac-air.com'}
                                    </a>
                                </div>
                            </div>
                            <div className='flex items-center -mr-2 sm:hidden'>
                                {/* Mobile menu button */}
                                <Disclosure.Button className='inline-flex justify-center items-center p-2 text-gray-400 rounded-md hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-primary'>
                                    <span className='sr-only'>Open main menu</span>
                                    {open ? (
                                        <XMarkIcon className='block w-6 h-6' aria-hidden='true' />
                                    ) : (
                                        <Bars3Icon className='block w-6 h-6' aria-hidden='true' />
                                    )}
                                </Disclosure.Button>
                            </div>
                        </div>
                    </div>

                    <Disclosure.Panel className='sm:hidden'>
                        <div className='px-4 pt-3 pb-2 border-b border-gray-200'>
                            <p className='text-sm font-medium text-gray-700'>Welcome, {currentUser?.Name || 'User'}</p>
                            <p className='text-xs text-gray-500 truncate' title={currentUser?.Account?.Name || ''}>
                                {currentUser?.Account?.Name || '—'}
                            </p>
                        </div>
                        <div className='pt-2 pb-3 space-y-1'>
                            <div
                                onClick={() => locationReload('/')}
                                className={classNames(
                                    window.location.pathname === '/'
                                        ? 'border-blue-secondary bg-blue-50 text-indigo-700'
                                        : 'border-transparent text-gray-500',
                                    'block border-l-4  py-2 pl-3 pr-4 text-base font-medium cursor-pointer'
                                )}
                            >
                                Parts Search
                            </div>
                            <div
                                onClick={() => locationReload('/quotes')}
                                className={classNames(
                                    window.location.pathname === '/quotes'
                                        ? 'border-blue-secondary bg-blue-50 text-indigo-700'
                                        : 'border-transparent text-gray-500',
                                    'block border-l-4  py-2 pl-3 pr-4 text-base font-medium '
                                )}
                            >
                                Parts Search History
                            </div>
                            <div
                                onClick={() => locationReload('/orders')}
                                className={classNames(
                                    window.location.pathname === '/orders'
                                        ? 'border-blue-secondary bg-blue-50 text-indigo-700'
                                        : 'border-transparent text-gray-500',
                                    'block border-l-4  py-2 pl-3 pr-4 text-base font-medium '
                                )}
                            >
                                My Orders
                            </div>
                        </div>
                        <div className='pt-4 pb-3 border-t border-gray-200'>
                            <div className='mt-3 space-y-1'>
                                <Link to='/settings?tab=Account'>
                                    <Disclosure.Button className='block px-4 py-2 w-full text-base font-medium text-left text-gray-500 hover:bg-gray-100 hover:text-gray-800'>
                                        My Account
                                    </Disclosure.Button>
                                </Link>
                                {accountsForChooser.length > 0 && (
                                    <Disclosure.Button
                                        onClick={() => {
                                            setSwitchOpen(true);
                                            setErrorMsg('');
                                        }}
                                        className='block px-4 py-2 w-full text-base font-medium text-left text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                                    >
                                        Switch account
                                    </Disclosure.Button>
                                )}
                                <Disclosure.Button
                                    as='a'
                                    href='#'
                                    onClick={logout}
                                    className='block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                                >
                                    Sign out
                                </Disclosure.Button>
                            </div>
                        </div>
                    </Disclosure.Panel>

                    <AccountChooserModal
                        isOpen={switchOpen}
                        contacts={accountsForChooser}
                        onCancel={() => setSwitchOpen(false)}
                        onConfirm={handleConfirmAccount}
                    />
                </>
            )}
        </Disclosure>
    );
}
